import React, { useEffect, useState } from "react";
import api from "../../utils/api";
import "./Graduatedproject.css";
import { FaExternalLinkAlt, FaBriefcase, FaGraduationCap, FaQuoteRight } from "react-icons/fa";

const GraduatedProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const response = await api.get("/graduate-projects");
        // Our Laravel resource nests array under response.data.data
        if (response.data && response.data.data) {
          setProjects(response.data.data);
        } else {
          setProjects(response.data || []);
        }
      } catch (err) { 
        setError("Unable to load graduate projects. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  return (
    <div className="graduated-projects-page">
      {/* Hero Section */}
      <section className="graduated-projects-hero">
        <div className="graduated-projects-hero-grid" aria-hidden="true" />
        <div className="graduated-projects-hero-content">
          <div className="graduated-projects-hero-badge">
            <span className="badge-dot" />
            Alumni Success Showcase
          </div>
          <h1 className="graduated-projects-hero-title">
            Where Our <span>Graduates</span> Work
          </h1>
          <p className="graduated-projects-hero-subtitle">
            Discover how GritinAI alumni are driving transformation, designing
            innovative products, and building high-impact careers in AI and tech
            across Africa.
          </p>
        </div>
      </section>

      {/* Main Content Area */}
      <main className="graduated-projects-main">
        {loading ? (
          // Skeletons
          <div className="graduated-projects-grid">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="graduated-project-skeleton-card">
                <div className="skeleton-avatar" />
                <div className="skeleton-line title-line" />
                <div className="skeleton-line badge-line" />
                <div className="skeleton-line text-line" />
                <div className="skeleton-line text-line short" />
                <div className="skeleton-btn" />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="graduated-projects-error">
            <div className="error-icon">⚠️</div>
            <h3>Something went wrong</h3>
            <p>{error}</p>
            <button className="retry-btn" onClick={() => window.location.reload()}>
              Retry
            </button>
          </div>
        ) : projects.length === 0 ? (
          <div className="graduated-projects-empty">
            <div className="empty-icon">
              <FaGraduationCap />
            </div>
            <h3>No Student Projects Yet</h3>
            <p>Our talented graduates are currently wrapping up their capstone projects and engineering portfolios. Check back soon to discover their groundbreaking work!</p>
          </div>
        ) : (
          <div className="graduated-projects-grid">
            {projects.map((project) => (
              <div key={project.id} className="graduated-project-premium-card">
                <div className="card-top-accent" />
                <div className="card-inner">
                  {/* Backdrop Quote Icon */}
                  <div className="card-quote-icon">
                    <FaQuoteRight />
                  </div>

                  {/* Graduate Identity */}
                  <div className="graduate-identity">
                    {project.image ? (
                      <div className="graduate-avatar-container">
                        <img
                          src={project.image}
                          alt={project.name}
                          className="graduate-avatar"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = "none";
                            const fallback = e.target.nextSibling;
                            if (fallback) fallback.style.display = "flex";
                          }}
                        />
                        <div className="graduate-avatar-fallback" style={{ display: "none" }}>
                          {project.name.substring(0, 2).toUpperCase()}
                        </div>
                      </div>
                    ) : (
                      <div className="graduate-avatar-fallback">
                        {project.name.substring(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div className="graduate-meta">
                      <h3 className="graduate-name">{project.name}</h3>
                      <div className="graduate-badge">
                        <FaBriefcase className="badge-icon" />
                        <span>{project.title}</span>
                      </div>
                    </div>
                  </div>

                  {/* Description Testimony */}
                  <p className="graduate-description">{project.description}</p>

                  {/* Action Link */}
                  {project.link && (
                    <div className="graduate-action">
                      <a
                        href={project.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="graduate-link-btn"
                      >
                        View Project / Profile
                        <FaExternalLinkAlt className="btn-arrow" />
                      </a>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default GraduatedProjects;
